// Compresión de imágenes cliente-side con Canvas. Sin librerías externas.
// - Detecta orientación EXIF (nativo del browser al usar bitmap loader).
// - Redimensiona al lado mayor MAX_SIDE px y re-encodea JPEG con quality.
// - Salta compresión si el archivo ya es pequeño y compatible.

const MAX_SIDE = 1600;   // px; suficiente para catálogo de cartas
const QUALITY = 0.82;    // JPEG quality
const SIZE_SKIP = 350 * 1024;  // <= 350KB → no comprimir

/** Devuelve { file, originalSize, compressedSize, skipped, reason } o lanza. */
export async function compressImage(file) {
  if (!file || !(file instanceof File)) {
    throw new Error("Archivo inválido");
  }
  if (!file.type.startsWith("image/") && !/\.(heic|heif|jpg|jpeg|png|webp)$/i.test(file.name)) {
    throw new Error(`Formato no soportado: ${file.type || file.name.split(".").pop()}`);
  }
  const originalSize = file.size;
  // Si ya pesa poco y es JPEG/PNG/WebP, no re-comprimir
  if (originalSize <= SIZE_SKIP && /jpeg|jpg|png|webp/i.test(file.type)) {
    return { file, originalSize, compressedSize: originalSize, skipped: true };
  }

  // HEIC/HEIF no se puede decodificar con canvas → mandamos original y que el backend haga la conversión
  if (/heic|heif/i.test(file.type) || /\.hei[cf]$/i.test(file.name)) {
    return { file, originalSize, compressedSize: originalSize, skipped: true, reason: "heic" };
  }

  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch (e) {
    // Fallback: intentamos con <img>
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
      reader.readAsDataURL(file);
    });
    bitmap = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("La imagen no se pudo decodificar. ¿Está dañada?"));
      img.src = dataUrl;
    });
  }

  const w = bitmap.width;
  const h = bitmap.height;
  const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
  const targetW = Math.round(w * scale);
  const targetH = Math.round(h * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas no disponible en este navegador");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("La compresión falló"))),
      "image/jpeg",
      QUALITY,
    );
  });

  // Si por alguna razón salió más pesado, devolvemos el original
  if (blob.size >= originalSize) {
    return { file, originalSize, compressedSize: originalSize, skipped: true, reason: "no-gain" };
  }

  const newName = (file.name.replace(/\.[^.]+$/, "") || "img") + ".jpg";
  const compressed = new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
  return { file: compressed, originalSize, compressedSize: blob.size, skipped: false };
}

/** Convierte un error de axios/fetch en mensaje humano. */
export function humanUploadError(err) {
  if (!err) return "Error desconocido";
  // Error nativo (compresión, lectura de archivo)
  if (err instanceof Error && !err.response && !err.request) {
    return err.message;
  }
  // Axios
  if (err.code === "ECONNABORTED") return "Se agotó el tiempo de espera. Revisa tu conexión.";
  if (err.message === "Network Error" || err.code === "ERR_NETWORK") {
    return "Sin conexión al servidor. Verifica tu internet.";
  }
  const status = err.response?.status;
  const detail = err.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (status === 413) return "La imagen pesa demasiado (máx 15 MB).";
  if (status === 415) return "Formato no soportado. Usa JPG, PNG, WEBP o HEIC.";
  if (status === 401) return "Sesión expirada. Vuelve a iniciar sesión.";
  if (status === 403) return "No tienes permiso para subir esta imagen.";
  if (status >= 500) return "Error del servidor al procesar la imagen. Intenta de nuevo.";
  return err.message || "No se pudo subir la imagen";
}
